import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { Refresh, Add, ArrowDownward, ArrowUpward, SortByAlpha, VerifiedUser, Rule, ShowChart, Tag } from '@mui/icons-material';
import GroupedMeterTile from './GroupedMeterTile';
import MeterFactorChart from './MeterFactorChart';
import { useAuth0 } from '@auth0/auth0-react';

interface MeterProduct {
  meter_product_id: string;
  meter_id: string;
  product_type: string;
  flow_label?: string | null;   // 'HIGH' | 'LOW' | 'MID…' for multi-flow meters
  display_name: string;
  meter_direction?: 'inbound' | 'outbound';
  meter_type?: 'custody' | 'check';
  current_health: 'red' | 'yellow' | 'green' | 'unknown';
  /** Health after acknowledgement suppression -- what the UI shows. current_health stays the truth. */
  display_health?: 'red' | 'yellow' | 'green' | 'unknown';
  /** Present only while an acknowledgement is active on this track. */
  acknowledgement?: {
    by?: string;
    at?: string;
    reason_code?: string;
    reason_label?: string;
    note?: string;
    expires_at?: string;
    consecutive?: number;
  };
  health_reasons?: string[];
  current_meter_factor: number;
  previous_meter_factor: number;
  control_limits: {
    ucl: number;
    lcl: number;
    center: number;
  };
  statistics: {
    document_count: number;
    avg_meter_factor: number;
    trend: 'increasing' | 'decreasing' | 'stable' | 'unknown';
    anomaly_count: number;
    consecutive_out_of_control?: number;
    days_since_proving: number;
  };
  last_proving_date?: string;
  grid_position?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  // Spike and Bias detection fields
  spike_detected?: boolean;
  spike_count?: number;
  spike_details?: {
    timestamp: string;
    current_value: number;
    previous_value: number;
    change_percent: number;
  };
  bias_detected?: boolean;
  bias_details?: {
    timestamp: string;
    spike_value: number;
    consecutive_count: number;
    severity: 'high' | 'medium' | 'low';
  };
  anomaly_type?: 'none' | 'spike' | 'bias';
  requires_investigation?: boolean;
  regression_drift?: any;
  repeatability?: any;
  adjustment?: any;
}

interface MergeDialogProps {
  open: boolean;
  source?: MeterProduct;
  target?: MeterProduct;
  onConfirm: () => void;
  onCancel: () => void;
}

const MergeDialog: React.FC<MergeDialogProps> = ({ open, source, target, onConfirm, onCancel }) => {
  if (!source || !target) return null;

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Merge Meter-Product Combinations?</DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          Are you sure you want to merge "{source.display_name}" into "{target.display_name}"?
        </Typography>
        <Alert severity="warning">
          This will combine all {source.statistics.document_count} documents from {source.display_name} with
          the {target.statistics.document_count} documents in {target.display_name}.
          The {source.display_name} tile will be removed.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="warning">
          Merge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Persist the Meter Health Monitor filter/sort selections across reloads, the
// same way the dashboard remembers section order/collapsed state.
const FILTER_STORAGE_KEY = 'meterHealthMonitorFilters';
const getSavedFilters = (): { sortDir?: string; productFilter?: string; healthFilter?: string } => {
  try {
    return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const MeterGrid: React.FC = () => {
  const { getIdTokenClaims } = useAuth0();
  const [meterProducts, setMeterProducts] = useState<MeterProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Filter / sort controls for the fixed-size reflow grid (restored from storage).
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    () => (getSavedFilters().sortDir === 'desc' ? 'desc' : 'asc')
  );
  const [productFilter, setProductFilter] = useState<string>(
    () => getSavedFilters().productFilter || 'all'
  );
  const [healthFilter, setHealthFilter] = useState<'all' | 'red' | 'yellow' | 'green' | 'acknowledged'>(
    () => {
      const h = getSavedFilters().healthFilter;
      return h === 'red' || h === 'yellow' || h === 'green' || h === 'acknowledged' ? h : 'all';
    }
  );

  // Bulk view mode: when set, forces EVERY card to numbers or charts at once.
  // null = each card uses its own saved preference. Persisted so the choice sticks.
  const [bulkViewMode, setBulkViewMode] = useState<'numbers' | 'chart' | null>(() => {
    const v = localStorage.getItem('meter-bulk-view');
    return v === 'numbers' || v === 'chart' ? v : null;
  });
  useEffect(() => {
    if (bulkViewMode) localStorage.setItem('meter-bulk-view', bulkViewMode);
    else localStorage.removeItem('meter-bulk-view');
  }, [bulkViewMode]);

  // Persist filter/sort selections whenever they change.
  useEffect(() => {
    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({ sortDir, productFilter, healthFilter })
    );
  }, [sortDir, productFilter, healthFilter]);

  // If a restored product filter no longer matches any loaded product, reset it
  // so a stale selection never hides the whole grid.
  useEffect(() => {
    if (
      productFilter !== 'all' &&
      meterProducts.length > 0 &&
      !meterProducts.some((mp) => mp.product_type === productFilter)
    ) {
      setProductFilter('all');
    }
  }, [meterProducts, productFilter]);

  // Same guard for the Acknowledged filter: the last acknowledgement expiring (or being revoked) must not
  // leave the user staring at an empty grid with a filter button that is no longer rendered.
  useEffect(() => {
    if (
      healthFilter === 'acknowledged' &&
      meterProducts.length > 0 &&
      !meterProducts.some((mp) => !!mp.acknowledgement)
    ) {
      setHealthFilter('all');
    }
  }, [meterProducts, healthFilter]);
  const [mergeDialog, setMergeDialog] = useState<{
    open: boolean;
    source?: MeterProduct;
    target?: MeterProduct;
  }>({ open: false });
  const [chartDialog, setChartDialog] = useState<{
    open: boolean;
    meterProductId?: string;
    meterName?: string;
    productType?: string;
  }>({ open: false });

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

  // Fetch on mount. The grid reflows via CSS, so nothing is gated on measurement.
  useEffect(() => {
    fetchMeterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the tiles live: poll, refetch when the tab regains focus, and recover
  // automatically when the network comes back (transient DNS/offline blips) --
  // so a dropped connection self-heals instead of needing a manual page refresh.
  useEffect(() => {
    const interval = setInterval(() => { fetchMeterData(); }, 45000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchMeterData();
    };
    const onOnline = () => { fetchMeterData(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchMeterData = async () => {
    try {
      setError(null);
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await fetch(`${API_ENDPOINT}/meters/dashboard`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch meter data');
      }

      const data = await response.json();

      // Handle both direct response and wrapped response from API Gateway
      let result;
      if (data.body) {
        // Response is wrapped by API Gateway
        result = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      } else {
        // Direct response
        result = data;
      }

      const products = result.meter_products || [];
      setMeterProducts(products);
    } catch (err) {
      console.error('Error fetching meter data:', err);
      setError('Failed to load meter data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await fetch(`${API_ENDPOINT}/meters/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('Refresh failed with status:', response.status, 'Error:', errorText);
        throw new Error(`Refresh failed: ${response.status}`);
      }

      await fetchMeterData();
    } catch (err) {
      console.error('Error refreshing meters:', err);
      alert(`Failed to refresh meters: ${err}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewChart = (meterProductId: string, meterId: string, productType: string) => {
    setChartDialog({
      open: true,
      meterProductId,
      meterName: meterId,
      productType,
    });
  };

  const handleDirectionChange = (meterId: string, direction: 'inbound' | 'outbound') => {
    // Force a data refresh - fetchMeterData will detect the direction change
    // and automatically recalculate layouts as needed
    fetchMeterData();
  };

  const handleTypeChange = (meterId: string, meterType: 'custody' | 'check') => {
    // Force a data refresh so the card moves into its Custody/Check sub-group.
    fetchMeterData();
  };

  const handleViewModeChange = (meterId: string, mode: 'numbers' | 'chart') => {
    // All cards maintain 1 grid unit size - the content changes, not the grid size
    // No layout changes needed since cards stay the same size
        // Debug log removed
  };

  const handleMerge = async () => {
    if (!mergeDialog.source || !mergeDialog.target) return;

    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await fetch(`${API_ENDPOINT}/meters/merge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_id: mergeDialog.source.meter_product_id,
          target_id: mergeDialog.target.meter_product_id,
        }),
      });

      if (response.ok) {
        // Refresh data after merge
        await fetchMeterData();
      }
    } catch (err) {
      console.error('Error merging meters:', err);
    } finally {
      setMergeDialog({ open: false });
    }
  };

  // Counts follow the DISPLAYED health so this page agrees with the dashboard tiles: an acknowledged
  // warning counts as Healthy in both, and the acknowledged total is shown separately.
  const shownHealth = (mp: MeterProduct) => mp.display_health || mp.current_health;
  const healthCounts = {
    red: meterProducts.filter(mp => shownHealth(mp) === 'red').length,
    yellow: meterProducts.filter(mp => shownHealth(mp) === 'yellow').length,
    green: meterProducts.filter(mp => shownHealth(mp) === 'green').length,
    acknowledged: meterProducts.filter(mp => !!mp.acknowledgement).length,
  };

  // Fixed card size; the grid reflows to as many columns as fit, down to 1.
  const CARD_WIDTH = 310;
  const CARD_HEIGHT = 330;

  // Distinct product types (for the product filter dropdown).
  const distinctProducts = Array.from(
    new Set(meterProducts.map(mp => mp.product_type))
  ).sort((a, b) => a.localeCompare(b));

  // A meter (its product list) passes the active filters if it has a product that
  // satisfies BOTH the product and health filters at once.
  const healthPasses = (mp: MeterProduct) =>
    healthFilter === 'all' ? true
      : healthFilter === 'acknowledged' ? !!mp.acknowledgement
      : shownHealth(mp) === healthFilter;

  const meterPasses = (products: MeterProduct[]) =>
    products.some(mp =>
      (productFilter === 'all' || mp.product_type === productFilter) &&
      healthPasses(mp)
    );

  // Which product tab to auto-select on a tile: the filtered product, else the
  // product matching the health filter, else none (default first tab).
  const focusFor = (products: MeterProduct[]): string | undefined => {
    if (productFilter !== 'all') return productFilter;
    if (healthFilter !== 'all') {
      return products.find(healthPasses)?.product_type;
    }
    return undefined;
  };

  // Exact track (meter_product_id) to auto-select. Preferred over focusFor because
  // multi-flow HIGH/LOW tracks SHARE a base product_type -- matching by base would land
  // on the first track, not the specific one that actually triggered the filter. Picks
  // the first track satisfying whichever filters are active.
  const focusMpidFor = (products: MeterProduct[]): string | undefined => {
    const byBoth = productFilter !== 'all' && healthFilter !== 'all';
    if (byBoth) {
      return products.find(mp => mp.product_type === productFilter && healthPasses(mp))?.meter_product_id;
    }
    if (healthFilter !== 'all') {
      return products.find(healthPasses)?.meter_product_id;
    }
    if (productFilter !== 'all') {
      return products.find(mp => mp.product_type === productFilter)?.meter_product_id;
    }
    return undefined;
  };

  // Map API products to the tile's prop shape.
  const mapTileProducts = (products: MeterProduct[]) =>
    products.map((mp: MeterProduct) => ({
      productType: mp.product_type,
      meterProductId: mp.meter_product_id,
      flowLabel: mp.flow_label || undefined,
      health: mp.current_health,
      displayHealth: mp.display_health,
      acknowledgement: mp.acknowledgement,
      healthReasons: mp.health_reasons,
      currentFactor: mp.current_meter_factor,
      previousFactor: mp.previous_meter_factor,
      controlLimits: mp.control_limits,
      statistics: {
        documentCount: mp.statistics.document_count,
        avgMeterFactor: mp.statistics.avg_meter_factor,
        trend: mp.statistics.trend,
        // "consecutive" badge = points outside control limits ending at the most
        // recent proving (0 once the latest reading is back in range).
        anomalyCount: mp.statistics.consecutive_out_of_control ?? 0,
        daysSinceProving: mp.statistics.days_since_proving,
      },
      lastProvingDate: mp.last_proving_date,
      spikeDetected: mp.spike_detected,
      spikeCount: mp.spike_count,
      spikeDetails: mp.spike_details ? {
        changePercent: mp.spike_details.change_percent,
        timestamp: mp.spike_details.timestamp,
      } : undefined,
      biasDetected: mp.bias_detected,
      biasDetails: mp.bias_details ? {
        severity: mp.bias_details.severity,
        consecutiveCount: mp.bias_details.consecutive_count,
      } : undefined,
      anomalyType: mp.anomaly_type,
      drift: mp.regression_drift,
      repeatability: mp.repeatability,
      adjustment: mp.adjustment,
    }));

  // Fixed-size reflow grid for a set of meter entries.
  const renderCardGrid = (
    entries: [string, MeterProduct[]][],
    direction: 'inbound' | 'outbound'
  ) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, ${CARD_WIDTH}px)`,
        gap: 2,
        justifyContent: 'start',
      }}
    >
      {entries.map(([meterId, products]) => (
        <Box key={meterId} sx={{ width: CARD_WIDTH, height: CARD_HEIGHT }}>
          <GroupedMeterTile
            meterId={meterId}
            meterDirection={direction}
            meterType={products[0]?.meter_type || 'custody'}
            focusProduct={focusFor(products)}
            focusMpid={focusMpidFor(products)}
            onViewChart={handleViewChart}
            onDirectionChange={handleDirectionChange}
            onTypeChange={handleTypeChange}
            onViewModeChange={handleViewModeChange}
            onMeterChanged={fetchMeterData}   // maintenance added/deleted -> refetch in place (no page reload)
            forceViewMode={bulkViewMode ?? undefined}
            products={mapTileProducts(products)}
          />
        </Box>
      ))}
    </Box>
  );

  // A direction section, sub-grouped into Custody Transfer and Check.
  const renderSection = (
    entries: [string, MeterProduct[]][],
    direction: 'inbound' | 'outbound'
  ) => {
    const typeOf = (products: MeterProduct[]) => products[0]?.meter_type || 'custody';
    const custody = entries.filter(([, p]) => typeOf(p) === 'custody');
    const check = entries.filter(([, p]) => typeOf(p) === 'check');

    const subGroup = (
      label: string,
      list: [string, MeterProduct[]][],
      icon: React.ReactNode
    ) =>
      list.length === 0 ? null : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            {icon}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}
            >
              {label}
            </Typography>
            <Chip label={list.length} size="small" variant="outlined" />
          </Box>
          {renderCardGrid(list, direction)}
        </Box>
      );

    return (
      <>
        {subGroup('Custody Transfer', custody, <VerifiedUser fontSize="small" color="success" />)}
        {subGroup('Check', check, <Rule fontSize="small" color="secondary" />)}
      </>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ width: '100%', overflow: 'visible' }}>
          {loading ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Meter Health Monitor</Typography>
                <Skeleton variant="circular" width={32} height={32} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, ${CARD_WIDTH}px)`, gap: 2, justifyContent: 'start' }}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} variant="rectangular" width={CARD_WIDTH} height={CARD_HEIGHT} />
                ))}
              </Box>
            </>
          ) : error && meterProducts.length === 0 ? (
            // Only take over the whole panel when we have NOTHING to show. A
            // transient blip while data is already loaded keeps the grid visible
            // and self-recovers on the next poll / on network return.
            <>
              <Alert severity="error">{error}</Alert>
              <Button onClick={fetchMeterData} sx={{ mt: 2 }}>
                Retry
              </Button>
            </>
          ) : (
            <>
              {/* Toolbar: health filter + product filter + sort + refresh */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={healthFilter}
            onChange={(_e, v) => setHealthFilter(v || 'all')}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="red" sx={{ color: 'error.main' }}>Critical {healthCounts.red}</ToggleButton>
            <ToggleButton value="yellow" sx={{ color: 'warning.main' }}>Warning {healthCounts.yellow}</ToggleButton>
            <ToggleButton value="green" sx={{ color: 'success.main' }}>Healthy {healthCounts.green}</ToggleButton>
            {/* Only offered once something IS acknowledged -- an always-on "Acknowledged 0" would be noise. */}
            {healthCounts.acknowledged > 0 && (
              <ToggleButton value="acknowledged" sx={{ color: 'info.main' }}>
                Acknowledged {healthCounts.acknowledged}
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Bulk view toggle: force ALL cards to numbers or charts. Deselect
                (click the active one) to hand control back to each card. */}
            <Tooltip title="Show all cards as numbers or as control charts">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={bulkViewMode}
                onChange={(_e, v) => setBulkViewMode(v)}
              >
                <ToggleButton value="numbers"><Tag fontSize="small" sx={{ mr: 0.5 }} />Numbers</ToggleButton>
                <ToggleButton value="chart"><ShowChart fontSize="small" sx={{ mr: 0.5 }} />Charts</ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="product-filter-label">Product</InputLabel>
              <Select
                labelId="product-filter-label"
                label="Product"
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
              >
                <MenuItem value="all">All Products</MenuItem>
                {distinctProducts.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={sortDir === 'asc' ? 'Sort Z → A' : 'Sort A → Z'}>
              <IconButton onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
                <SortByAlpha sx={{ transform: sortDir === 'desc' ? 'scaleY(-1)' : 'none' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Meter grid: fixed-size cards that reflow columns, split by direction. */}
        {meterProducts.length === 0 ? (
          <Alert severity="info">
            No meters found. Upload proving reports to see meter health status.
          </Alert>
        ) : (() => {
          // Group by meter, split by direction, then apply filters + sort.
          const grouped = meterProducts.reduce((acc: { [k: string]: MeterProduct[] }, mp) => {
            if (!acc[mp.meter_id]) acc[mp.meter_id] = [];
            acc[mp.meter_id].push(mp);
            return acc;
          }, {} as { [k: string]: MeterProduct[] });
          const inbound: [string, MeterProduct[]][] = [];
          const outbound: [string, MeterProduct[]][] = [];
          Object.entries(grouped).forEach(([meterId, products]) => {
            if (!meterPasses(products)) return;
            const dir = products[0]?.meter_direction || 'inbound';
            (dir === 'outbound' ? outbound : inbound).push([meterId, products]);
          });
          const cmp = (a: [string, MeterProduct[]], b: [string, MeterProduct[]]) =>
            sortDir === 'asc' ? a[0].localeCompare(b[0]) : b[0].localeCompare(a[0]);
          inbound.sort(cmp);
          outbound.sort(cmp);

          if (inbound.length === 0 && outbound.length === 0) {
            return (
              <Alert severity="info">No meters match the current filters.</Alert>
            );
          }

          return (
            <Box sx={{ minHeight: 400 }}>
              {inbound.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ArrowDownward color="primary" />
                    <Typography variant="h6" color="primary">Inbound Meters</Typography>
                    <Chip label={`${inbound.length} meter${inbound.length !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" />
                  </Box>
                  {renderSection(inbound, 'inbound')}
                </Box>
              )}
              {inbound.length > 0 && outbound.length > 0 && <Divider sx={{ my: 3 }} />}
              {outbound.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ArrowUpward color="secondary" />
                    <Typography variant="h6" color="secondary">Outbound Meters</Typography>
                    <Chip label={`${outbound.length} meter${outbound.length !== 1 ? 's' : ''}`} size="small" color="secondary" variant="outlined" />
                  </Box>
                  {renderSection(outbound, 'outbound')}
                </Box>
              )}
            </Box>
          );
        })()}

        {/* Merge Dialog */}
        <MergeDialog
          open={mergeDialog.open}
          source={mergeDialog.source}
          target={mergeDialog.target}
          onConfirm={handleMerge}
          onCancel={() => setMergeDialog({ open: false })}
        />

              {/* Chart Dialog */}
              <Dialog
                open={chartDialog.open}
                onClose={() => setChartDialog({ open: false })}
                maxWidth="lg"
                fullWidth
              >
                <DialogContent>
                  {chartDialog.meterProductId && (
                    <MeterFactorChart
                      meterProductId={chartDialog.meterProductId}
                      meterName={chartDialog.meterName}
                      productType={chartDialog.productType}
                      onClose={() => setChartDialog({ open: false })}
                    />
                  )}
                </DialogContent>
              </Dialog>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MeterGrid;